import { getUploadManager } from "./manager";
import { useUploadQueueStore } from "~/stores/uploadQueueStore";
import type { UploadSlot } from "./types";

export type {
  UploadSlot,
  UploadRecord,
  SlotStatus,
  ProcessingStatus,
  VideoPayload,
  VideoRenditionPayload,
  OriginalSourcePayload,
} from "./types";
export { getUploadManager } from "./manager";
export { capturePreviewFrame, revokePreviewUrl } from "./previewFrame";

// Enqueue a file for resumable upload. Returns the immediate placeholder slot
// (upload pending → uploading → processing) so the caller can render a slot
// straight away instead of blocking on the transfer. The slot is live: read
// updates for it via `useUploadSlots()` / the queue store, keyed by `slot.id`.
// Returns null when the file was NOT enqueued (oversize rejection, which lands
// its own visible error slot, or a duplicate, which toasts) — the caller keeps
// iterating the rest of the batch rather than aborting.
export function enqueueUpload(file: File): UploadSlot | null {
  const manager = getUploadManager();
  const id = manager.addFile(file);
  if (id == null) return null;
  // file-added has already upserted the slot synchronously.
  const slot = useUploadQueueStore.getState().slots[id];
  return (
    slot ?? {
      id,
      name: file.name,
      size: file.size,
      status: "pending",
      progress: 0,
      bytesUploaded: 0,
      bytesPerSecond: null,
      etaSeconds: null,
      errorMessage: null,
      upload: null,
      uploadSessionId: null,
      previewUrl: null,
    }
  );
}

export function pauseUpload(id: string): void {
  getUploadManager().pause(id);
}

export function resumeUpload(id: string): void {
  getUploadManager().resume(id);
}

export function cancelUpload(id: string): void {
  getUploadManager().cancel(id);
}

// X on a terminal row: removes the row and hard-deletes the ingested Upload
// (R2 bytes + DB rows) when one exists. Straight-up delete — no confirm.
export function removeUpload(id: string): void {
  getUploadManager().remove(id);
}

// Retry a failed or watchdog-stalled slot. Routes through the manager so the
// components never reach into the Uppy instance directly.
export function retryUpload(id: string): void {
  getUploadManager().retry(id);
}
